/**
 * Pixabay API Key 列表：超管设置页 UI 回填与提交规范化。
 * 后端字段 `pixabay_api_keys`；免费额度按数组顺序轮换。
 */

/** 提交成功时的规范化结果 */
export type PreparePixabayApiKeysSuccess = {
    /** 是否通过校验 */
    ok: true;
    /** 提交给后端的 key 数组（可为空数组） */
    keys: string[];
};

/** 提交失败（重复）时的结果 */
export type PreparePixabayApiKeysDuplicate = {
    /** 是否通过校验 */
    ok: false;
    /** 失败原因：存在重复 key */
    reason: 'duplicate';
};

/** 提交前校验与规范化的联合结果 */
export type PreparePixabayApiKeysResult =
    | PreparePixabayApiKeysSuccess
    | PreparePixabayApiKeysDuplicate;

/**
 * 将接口返回的 key 列表转为表单展示用数组。
 * 空 / undefined / null / 非数组时至少保留一行空输入。
 * @param keys - GET Admin 返回的 `pixabay_api_keys`
 * @returns 至少含一个元素的字符串数组
 */
export function normalizePixabayApiKeysForUi(keys?: string[] | null): string[] {
    if (!Array.isArray(keys) || keys.length === 0) {
        return [''];
    }
    // 保持后端顺序与内容；空串也原样展示，便于用户编辑
    return keys.map((key) => (typeof key === 'string' ? key : ''));
}

/**
 * 提交前规范化：trim → 非空项去重校验 → 去掉空串。
 * 全部为空时传 `[]`；大小写敏感，不做 toLowerCase。
 * @param keys - 表单当前行（可含空串）
 * @returns 成功返回可提交数组；有重复则 `ok: false`
 */
export function preparePixabayApiKeysForSubmit(
    keys: string[]
): PreparePixabayApiKeysResult {
    // 逐项 trim，非字符串按空串处理
    const trimmed = (Array.isArray(keys) ? keys : []).map((key) =>
        typeof key === 'string' ? key.trim() : ''
    );
    // 仅对非空项做重复校验（大小写敏感）
    const nonEmpty = trimmed.filter((key) => key.length > 0);
    const seen = new Set<string>();
    for (const key of nonEmpty) {
        if (seen.has(key)) {
            return { ok: false, reason: 'duplicate' };
        }
        seen.add(key);
    }
    return { ok: true, keys: nonEmpty };
}
